/**
 * Runtime type guards and validation utilities
 *
 * These utilities provide type-safe runtime checks for common scenarios,
 * helping prevent null/undefined errors and ensuring data integrity.
 */

/**
 * Type guard to check if a value is not null or undefined
 *
 * @example
 * const value: string | null | undefined = getValue();
 * if (isDefined(value)) {
 *   // value is string here
 *   console.log(value.toUpperCase());
 * }
 */
export function isDefined<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}

/**
 * Assert that a value is defined, throw error if not
 * Useful for cases where undefined values indicate a programming error
 *
 * @throws {Error} If value is null or undefined
 *
 * @example
 * const config = assertDefined(getConfig(), 'Configuration is required');
 * // config is guaranteed to be defined here
 */
export function assertDefined<T>(
  value: T | null | undefined,
  message: string = 'Value must be defined'
): asserts value is T {
  if (!isDefined(value)) {
    throw new Error(message);
  }
}

/**
 * Get value or return a default if undefined/null
 *
 * @example
 * const count = withDefault(maybeCount, 0);
 * const name = withDefault(maybeName, 'Anonymous');
 */
export function withDefault<T>(value: T | null | undefined, defaultValue: T): T {
  return isDefined(value) ? value : defaultValue;
}

/**
 * Type guard to check if a value is a non-empty array
 *
 * @example
 * const items: unknown[] = getItems();
 * if (isNonEmptyArray(items)) {
 *   // items.length > 0 is guaranteed
 *   console.log(items[0]);
 * }
 */
export function isNonEmptyArray<T>(value: unknown): value is [T, ...T[]] {
  return Array.isArray(value) && value.length > 0;
}

/**
 * Type guard to check if a value is a non-empty string
 * Trims the string before checking to catch whitespace-only strings
 *
 * @example
 * const input: unknown = getUserInput();
 * if (isNonEmptyString(input)) {
 *   // input is a non-empty string here
 *   processInput(input);
 * }
 */
export function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

/**
 * Type guard to check if a value is a number (not NaN)
 *
 * @example
 * const value: unknown = parseFloat(input);
 * if (isNumber(value)) {
 *   // value is a valid number
 *   calculate(value);
 * }
 */
export function isNumber(value: unknown): value is number {
  return typeof value === 'number' && !isNaN(value);
}

/**
 * Type guard to check if a value is a valid finite number (not NaN or Infinity)
 *
 * @example
 * const price: unknown = data.price;
 * if (isFiniteNumber(price)) {
 *   // price is a finite number
 *   const total = price * quantity;
 * }
 */
export function isFiniteNumber(value: unknown): value is number {
  return isNumber(value) && isFinite(value);
}

/**
 * Type guard to check if a value is a plain object (not array, null, or other types)
 *
 * @example
 * const data: unknown = JSON.parse(response);
 * if (isPlainObject(data)) {
 *   // data is an object
 *   processObject(data);
 * }
 */
export function isPlainObject(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    Object.prototype.toString.call(value) === '[object Object]'
  );
}

/**
 * Safe object property access with default value
 * Returns property value or default if property doesn't exist or is undefined
 *
 * @example
 * const user = { name: 'John' };
 * const name = safeGet(user, 'name', 'Anonymous'); // 'John'
 * const age = safeGet(user, 'age', 0); // 0
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function safeGet<T extends Record<string, any>, K extends keyof T>(
  obj: T,
  key: K,
  defaultValue: NonNullable<T[K]>
): NonNullable<T[K]> {
  const value = obj[key];
  return isDefined(value) ? value : defaultValue;
}

/**
 * Type guard to check if a value is a boolean
 *
 * @example
 * const value: unknown = data.enabled;
 * if (isBoolean(value)) {
 *   // value is boolean
 *   toggleFeature(value);
 * }
 */
export function isBoolean(value: unknown): value is boolean {
  return typeof value === 'boolean';
}

/**
 * Safely parse JSON with type checking
 * Returns parsed value or default if parsing fails or result doesn't match guard
 *
 * @example
 * const data = safeJsonParse(
 *   response,
 *   isPlainObject,
 *   {}
 * );
 */
export function safeJsonParse<T>(
  jsonString: string,
  typeGuard: (value: unknown) => value is T,
  defaultValue: T
): T {
  try {
    const parsed = JSON.parse(jsonString);
    return typeGuard(parsed) ? parsed : defaultValue;
  } catch {
    return defaultValue;
  }
}

/**
 * Filter array to only defined values
 * Removes null and undefined from arrays while maintaining type safety
 *
 * @example
 * const items = [1, null, 2, undefined, 3];
 * const defined = filterDefined(items); // [1, 2, 3]
 */
export function filterDefined<T>(array: (T | null | undefined)[]): T[] {
  return array.filter(isDefined);
}

/**
 * Ensure a value is an array, wrapping it if necessary
 *
 * @example
 * const single = 'item';
 * const array = ['item'];
 * ensureArray(single); // ['item']
 * ensureArray(array);  // ['item']
 * ensureArray(null);   // []
 */
export function ensureArray<T>(value: T | T[] | null | undefined): T[] {
  if (!isDefined(value)) return [];
  return Array.isArray(value) ? value : [value];
}
